import { rpcClient } from "eddev/hooks"
import { FerveCart } from "./ferve-cart"
import { FerveWishlist } from "./ferve-wishlist"
import { safeStorage } from "@utils/safeStorage"

type FerveConnectionSettings = {
  uri: string
  hash: string
  inCartDonations: boolean
}

const FERVE_CONFIG = {
  sff: {
    uri: env.dev
      ? `${env.client ? document.location.origin.replace(":8080", ":8787") : ""}/api/v1/`
      : env.client
        ? document.location.hostname === "www.sff.org.au"
          ? "https://tix.sff.org.au/api/v1/"
          : "https://tix-staging.sff.org.au/api/v1/"
        : "https://tix-staging.sff.org.au/api/v1/",
    hash: "a9dec309bc454490a48ecb52f05c57b6",
    inCartDonations: true,
  },
  tff: {
    uri: env.dev ? "https://sff2.local:8787/api/v1/" : "https://tff.sff.org.au/api/v1/",
    hash: "b581b5b0ed5143f3b59e9b2f5bafea6d",
    inCartDonations: true,
  },
} satisfies {
  [K in FerveMode]: FerveConnectionSettings
}

export type FerveMode = "sff" | "tff"

export type FerveSession = {
  mode: FerveMode
  hash: string
  loggedIn: boolean
  firstName: string
  memberType: any
  memberExpiry: any
  passType: any
  subscriptions: any
  cartCount: number
  cartQty: number
  cartTotal: number
}

export class FerveAPI {
  public conn: FerveConnectionSettings
  public wishlist: FerveWishlist
  public cart: FerveCart

  public session: FerveSession | null = null

  private disposer: (() => void)[] = []

  constructor(public mode: FerveMode) {
    this.mode = mode
    this.conn = FERVE_CONFIG[mode]
    this.wishlist = new FerveWishlist(this)
    this.cart = new FerveCart(this)
  }

  init() {
    // Keep checking session
    const sessionTimer = setInterval(
      () => {
        if (this.session) {
          this.activity()
        }
      },
      1000 * 60 * 5,
    )
    this.onDestroy(() => clearInterval(sessionTimer))

    // Load the session
    // this.sessionHash = safeStorage.getItem(`ferve_session_${this.mode}`)
    this.session = this.readCookie()
    if (this.session) this.activity()

    setTimeout(async () => {
      await this.getUserId()
    }, 100)
  }

  onDestroy(callback: () => void) {
    this.disposer.push(callback)
  }

  url(path: string) {
    return new URL(path, this.conn.uri).toString()
  }

  async activity() {
    await this.GET("Visitors/Activity")
    this.readCookie()
  }

  async getSessionHash() {
    this.session = this.readCookie()
    if (!this.session?.hash) {
      await this.GET("Visitors/Activity?hash=new")
      this.session = this.readCookie()
    }
    return this.session?.hash
  }

  destroy() {
    console.log("Destroying client")
    for (const fn of this.disposer) {
      fn()
    }
  }

  async GET(methodName: string, args?: any): Promise<any> {
    const params = new URLSearchParams(args ?? {})
    params.set("hash", this.conn.hash)
    if (this.session?.hash) params.set("hash", this.session?.hash)
    const uri = this.conn.uri + methodName.replace(/^\//, "") + "?" + params.toString()

    const response = await fetch(uri, {
      credentials: "include",
    })
    this.readCookie()
    const data = await response.json()
    return data
  }

  async POST(methodName: string, args?: any): Promise<any> {
    const params = new URLSearchParams({})
    params.set("hash", (await this.getSessionHash()) ?? "new")
    const uri = this.conn.uri + methodName.replace(/^\//, "") + "?" + params.toString()
    // console.info("POST", uri)

    const response = await fetch(uri, {
      method: "POST",
      body: JSON.stringify(args),
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
    this.readCookie()
    const data = await response.json()
    return data
  }

  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"))
    return match ? decodeURIComponent(match[2]) : null
  }

  /**
   * Gets the Ferve User ID via a flakey API call.
   */
  async getUserId() {
    if (!this.session?.hash) return null
    const storageKey = "ferve_user_id:" + this.session.hash
    let value = safeStorage.getItem(storageKey)
    if (value) {
      return value
    }
    const cookie = this.getCookie("ftapi_" + this.conn.hash)
    const result = await rpcClient.mutation("api.ferve.ferveUserId", {
      mode: this.mode,
      cookie: cookie ?? "",
      appHash: this.conn.hash,
    })
    if (typeof result === "string") {
      safeStorage.setItem(storageKey, result)
      return result
    }
  }

  readCookie(cookie?: string): FerveSession | null {
    cookie = cookie ?? this.getCookie("ftapi_" + this.conn.hash) ?? undefined
    if (!cookie) {
      return null
    }

    // Re-set cookie with 7 day expiry
    try {
      const expiryDate = new Date()
      expiryDate.setTime(expiryDate.getTime() + 7 * 24 * 60 * 60 * 1000) // 7 days
      const host = document.location.hostname
      const domain = host.includes("sff.org.au") ? ".sff.org.au" : host
      const secure = document.location.protocol === "https:" ? "Secure" : ""
      document.cookie = `ftapi_${this.conn.hash}=${cookie}; expires=${expiryDate.toUTCString()}; path=/; domain=${domain}; ${secure}`
    } catch (err) {}

    // Parse and return
    try {
      const main = cookie.split("**").map(atob)
      if (main.length === 0 || !main[0]) {
        throw new Error("Invalid session cookie data")
      }
      const user = atob(main[2])
        .replace(/[\r\n]/g, "")
        .split(";")
      this.session = {
        mode: this.mode,
        hash: main[0],
        loggedIn: main[1] == "1",
        firstName: user[1],
        memberType: user[2],
        memberExpiry: user[3],
        passType: user[4],
        subscriptions: user[5],
        cartCount: main[3] as any,
        cartQty: main[4] as any,
        cartTotal: main[5] as any,
      }
      return this.session
    } catch (err) {
      console.error(err)
    }
    return null
  }

  async login(email: string, password: string) {
    const result = await this.POST("Visitors/Login", {
      email,
      password,
    })
    if (result.Success) {
      this.readCookie()
      this.activity()
      this.wishlist.refresh()
      return true
    } else {
      const error = result?.Errors?.[0]?.CustomerMessage
      throw new Error(error ?? "An error occurred")
    }
  }

  get isLoggedIn() {
    return this.session?.loggedIn
  }

  async logout() {
    const result = await this.GET("Visitors/Logout")
    this.readCookie()
    this.activity()
    this.wishlist.refresh()
  }
}
